import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Copy, FlaskConical, HelpCircle, Loader2, Save,
} from 'lucide-react';

import { getDomains } from '@/services/api/domains';
import {
    createSetupsBulk, getSetups, simulateBulk, updateSetup,
} from '@/services/api/price-structure';
import { Button } from '@/components/ui/button';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { useToast } from '@/hooks/use-toast';
import type {
    BulkSimulateRow, CreateSetupInput, EngineVersion, LinearEngineParams, Setup, SetupBase,
} from '@/types/price-structure';

import { HelpModal } from './components/HelpModal';
import { DomainSetupCard } from './components/DomainSetupCard';
import { SimulationDetailModal } from './components/SimulationDetailModal';
import {
    DEFAULT_DIALS, DEFAULT_LINEAR_PARAMS, ENGINE_DEFAULTS, asArray, engineOf, linearParamsOf,
    pmrTargetOf,
} from './constants';

interface Row {
    domainId: number;
    domainName: string;
    existingSetupId: number | null;
    dials: typeof DEFAULT_DIALS;
}

export default function ConfigureGrid() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
    const [dialsByDomain, setDialsByDomain] = useState<Record<number, typeof DEFAULT_DIALS>>({});
    const [simulation, setSimulation] = useState<Record<number, BulkSimulateRow>>({});
    const [helpOpen, setHelpOpen] = useState(false);
    const [inspecting, setInspecting] = useState<number | null>(null);

    const { data: domains, isLoading: domainsLoading } = useQuery({
        queryKey: ['domains'],
        queryFn: getDomains,
    });

    const { data: setups } = useQuery({
        queryKey: ['price-structure-setups'],
        queryFn: getSetups,
    });

    const setupByDomain = useMemo(() => {
        const map = new Map<number, number>();
        asArray<Setup>(setups).forEach((s) => map.set(s.domain_id, s.id));
        return map;
    }, [setups]);

    const domainOptions = useMemo(
        () =>
            asArray<any>(domains).map((d) => ({
                value: String(d.id),
                label: d.domain,
                sublabel: setupByDomain.has(d.id) ? 'já tem setup' : undefined,
            })),
        [domains, setupByDomain],
    );

    const rows: Row[] = useMemo(
        () =>
            selectedDomains.map((id) => {
                const domainId = Number(id);
                const domain = asArray<any>(domains).find((d) => d.id === domainId);
                const existing = asArray<Setup>(setups).find((s) => s.domain_id === domainId);

                return {
                    domainId,
                    domainName: domain?.domain ?? `#${domainId}`,
                    existingSetupId: existing?.id ?? null,
                    dials:
                        dialsByDomain[domainId] ??
                        (existing
                            ? {
                                  engine_version: engineOf(existing),
                                  engine_params:
                                      engineOf(existing) === 'v4' ? linearParamsOf(existing) : null,
                                  aggressiveness: existing.aggressiveness,
                                  granularity: existing.granularity,
                                  type_source: existing.type_source,
                                  min_impressions: existing.min_impressions,
                                  days_sync: existing.days_sync,
                                  steps_count: existing.steps_count,
                                  pmr_target_override: existing.pmr_target_override ?? null,
                              }
                            : DEFAULT_DIALS),
                };
            }),
        [selectedDomains, domains, setups, dialsByDomain],
    );

    const setDial = (domainId: number, patch: Partial<typeof DEFAULT_DIALS>) => {
        const current = rows.find((r) => r.domainId === domainId)?.dials ?? DEFAULT_DIALS;
        // Trocar o motor traz junto os padrões dele (janela, volume, degraus):
        // os do v3 não fazem sentido no v4 e vice-versa.
        const engineChanged =
            patch.engine_version !== undefined && patch.engine_version !== current.engine_version;
        const next = engineChanged
            ? { ...current, ...ENGINE_DEFAULTS[patch.engine_version as EngineVersion], ...patch }
            : { ...current, ...patch };

        // Ao trocar a agressividade, um override que era só o alvo antigo deixa
        // de ser customização — volta a seguir o novo dial.
        if (
            patch.aggressiveness !== undefined &&
            next.pmr_target_override === pmrTargetOf(current.aggressiveness)
        ) {
            next.pmr_target_override = null;
        }

        setDialsByDomain((prev) => ({ ...prev, [domainId]: next }));
        // A simulação daquela linha deixa de valer assim que os dials mudam.
        setSimulation((prev) => {
            const next = { ...prev };
            delete next[domainId];
            return next;
        });
    };

    const applyFirstToAll = () => {
        if (rows.length < 2) return;
        const source = rows[0].dials;
        const next: Record<number, typeof DEFAULT_DIALS> = {};
        rows.forEach((r) => {
            next[r.domainId] = { ...source };
        });
        setDialsByDomain(next);
        setSimulation({});
        toast({
            title: 'Dials replicados',
            description: `Configuração de ${rows[0].domainName} aplicada a ${rows.length - 1} outro(s) domínio(s).`,
        });
    };

    const toPayload = (row: Row): SetupBase & { name: string } => ({
        domain_id: row.domainId,
        name: row.domainName,
        aggressiveness: row.dials.aggressiveness,
        granularity: row.dials.granularity,
        type_source: row.dials.type_source,
        // No v4 o corte é a receita mínima; o ms ignora este campo.
        min_impressions: row.dials.engine_version === 'v4' ? 0 : row.dials.min_impressions,
        days_sync: row.dials.days_sync,
        steps_count: row.dials.steps_count,
        pmr_target_override: row.dials.pmr_target_override,
        engine_version: row.dials.engine_version,
        // No v3 os parâmetros não existem; mandar null deixa o que estiver no banco.
        ...(row.dials.engine_version === 'v4'
            ? { engine_params: row.dials.engine_params ?? DEFAULT_LINEAR_PARAMS }
            : {}),
    });

    const setLinear = (row: Row, patch: Partial<LinearEngineParams>) =>
        setDial(row.domainId, {
            engine_params: { ...(row.dials.engine_params ?? DEFAULT_LINEAR_PARAMS), ...patch },
        });

    const simulateMutation = useMutation({
        mutationFn: () => simulateBulk(rows.map(toPayload)),
        onSuccess: (data) => {
            const byDomain: Record<number, BulkSimulateRow> = {};
            data.results.forEach((r) => {
                byDomain[r.domain_id] = r;
            });
            setSimulation(byDomain);

            const { ok, failed, segments_written } = data.totals;
            toast({
                title: 'Simulação concluída',
                description: `${ok} domínio(s) ok, ${failed} com erro, ${segments_written} segmentos gerariam granulação.`,
                variant: failed > 0 ? 'destructive' : undefined,
            });
        },
        onError: (error: any) =>
            toast({
                variant: 'destructive',
                title: 'Erro ao simular',
                description: error.response?.data?.detail || 'Falha ao consultar o GAM.',
            }),
    });

    const saveMutation = useMutation({
        mutationFn: async () => {
            const toCreate = rows.filter((r) => !r.existingSetupId);
            const toUpdate = rows.filter((r) => r.existingSetupId);

            const created = toCreate.length
                ? await createSetupsBulk(toCreate.map(toPayload) as CreateSetupInput[])
                : { results: [], totals: { created: 0, conflict: 0, failed: 0 } };

            // Atualização não tem endpoint em massa: cada setup existente é um PUT.
            const updated = await Promise.allSettled(
                toUpdate.map((r) => {
                    const { domain_id: _ignored, ...rest } = toPayload(r);
                    return updateSetup(r.existingSetupId as number, rest);
                }),
            );

            return {
                created: created.totals,
                updated: {
                    ok: updated.filter((u) => u.status === 'fulfilled').length,
                    failed: updated.filter((u) => u.status === 'rejected').length,
                },
            };
        },
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['price-structure-setups'] });
            const parts = [];
            if (result.created.created) parts.push(`${result.created.created} criado(s)`);
            if (result.updated.ok) parts.push(`${result.updated.ok} atualizado(s)`);
            if (result.created.conflict) parts.push(`${result.created.conflict} em conflito`);
            const failed = result.created.failed + result.updated.failed;
            if (failed) parts.push(`${failed} com falha`);

            toast({
                title: 'Setups salvos',
                description: parts.join(' · ') || 'Nada a fazer.',
                variant: failed ? 'destructive' : undefined,
            });
            if (!failed) navigate('/price-structure');
        },
        onError: (error: any) =>
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Falha ao gravar os setups.',
            }),
    });

    const simulatedCount = Object.keys(simulation).length;

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <button
                        onClick={() => navigate('/price-structure')}
                        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-2"
                    >
                        <ArrowLeft className="h-4 w-4" /> Voltar
                    </button>
                    <div className="flex items-center gap-2">
                        <h1 className="text-2xl font-semibold text-gray-900">
                            Configurar estrutura de preço
                        </h1>
                        <button
                            type="button"
                            onClick={() => setHelpOpen(true)}
                            title="Como funciona"
                            className="rounded-full p-1 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                        >
                            <HelpCircle className="h-5 w-5" />
                        </button>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                        Selecione os domínios, ajuste os dials de cada um e simule antes de salvar.
                        Um setup por domínio.
                    </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    <Button
                        variant="outline"
                        disabled={rows.length === 0 || simulateMutation.isPending}
                        onClick={() => simulateMutation.mutate()}
                    >
                        {simulateMutation.isPending ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <FlaskConical className="mr-2 h-4 w-4" />
                        )}
                        Simular todos
                    </Button>
                    <Button
                        disabled={rows.length === 0 || saveMutation.isPending}
                        onClick={() => saveMutation.mutate()}
                    >
                        {saveMutation.isPending ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="mr-2 h-4 w-4" />
                        )}
                        Salvar setups
                    </Button>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
                <label className="text-sm font-medium text-gray-700">Domínios</label>
                <SearchableMultiSelect
                    values={selectedDomains}
                    onValuesChange={setSelectedDomains}
                    options={domainOptions}
                    placeholder={domainsLoading ? 'Carregando domínios...' : 'Selecione um ou mais domínios'}
                    searchPlaceholder="Buscar domínio..."
                    disabled={domainsLoading}
                    selectedLabel={(n) => `${n} domínio(s) selecionado(s)`}
                />
                {rows.length > 1 && (
                    <Button variant="outline" size="sm" onClick={applyFirstToAll}>
                        <Copy className="mr-2 h-3.5 w-3.5" />
                        Aplicar dials de {rows[0].domainName} a todos
                    </Button>
                )}
            </div>

            {simulateMutation.isPending && (
                <div className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Consultando o GAM para {rows.length} domínio(s). Leva cerca de 8s por domínio,
                    até 6 em paralelo.
                </div>
            )}

            {rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                        <FlaskConical className="h-8 w-8 text-blue-500" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold text-gray-900">Nenhum domínio selecionado</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        Escolha os domínios acima para começar a configurar.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {rows.map((row) => (
                        <DomainSetupCard
                            key={row.domainId}
                            domainName={row.domainName}
                            dials={row.dials}
                            existingSetupId={row.existingSetupId}
                            result={simulation[row.domainId]}
                            onChange={(patch) => setDial(row.domainId, patch)}
                            onChangeLinear={(patch) => setLinear(row, patch)}
                            onInspect={() => setInspecting(row.domainId)}
                        />
                    ))}
                </div>
            )}

            {rows.some((r) => r.dials.engine_version === 'v4') && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-xs leading-relaxed text-blue-900">
                    <strong className="font-medium">Motor v4:</strong> a cascata abre no eCPM do
                    segmento × o multiplicador e desce em degraus iguais até o chão, sempre
                    arredondando para cima na escada de preços do GAM; com “0,05 no fim”, tenta
                    uma última venda a 0,05. Recalcula <strong>todo dia às 6h</strong>, corrigindo o
                    preço pelo preenchimento do dia anterior; a janela só define o eCPM que serve de
                    semente e de limite. Só muda o domínio desta linha — os outros continuam no
                    motor deles.
                </div>
            )}

            {simulatedCount > 0 && (
                <p className="text-xs text-gray-500">
                    Alterar qualquer dial invalida a simulação daquela linha — simule de novo antes de salvar.
                </p>
            )}

            <HelpModal open={helpOpen} onOpenChange={setHelpOpen} />

            <SimulationDetailModal
                open={inspecting !== null}
                onOpenChange={(open) => !open && setInspecting(null)}
                domainId={inspecting}
                domainName={rows.find((r) => r.domainId === inspecting)?.domainName ?? ''}
                typeSource={
                    rows.find((r) => r.domainId === inspecting)?.dials.type_source ??
                    DEFAULT_DIALS.type_source
                }
                aggressiveness={
                    rows.find((r) => r.domainId === inspecting)?.dials.aggressiveness ??
                    DEFAULT_DIALS.aggressiveness
                }
                granularity={
                    rows.find((r) => r.domainId === inspecting)?.dials.granularity ??
                    DEFAULT_DIALS.granularity
                }
                engineVersion={
                    rows.find((r) => r.domainId === inspecting)?.dials.engine_version ?? 'v3'
                }
                linearParams={
                    rows.find((r) => r.domainId === inspecting)?.dials.engine_params ?? null
                }
                result={inspecting !== null ? (simulation[inspecting] ?? null) : null}
            />
        </div>
    );
}

